/**
 * 设计师编辑器组合面板的操作状态，按面板图层 ID 订阅 VIP 水印服务和图层数据变化
 */
import { useCallback, useSyncExternalStore } from 'react';
import type { IEditor } from '@/instance';

/**
 * 在组合面板挂载时订阅操作权限，VIP 判定异步写回后刷新按钮；切换图层或卸载时解绑事件
 *
 * @param editor - 提供图层数据、组合权限和 VIP 水印事件的编辑器实例
 * @param layerId - 当前组合面板对应的图层 ID
 * @returns 组合、解组或禁用状态，图层不存在时返回 null
 */
export function useGroupAction(editor: IEditor, layerId: string) {
    const subscribe = useCallback(
        (onChange: () => void) => {
            const { events, flatLayer, svipWatermark } = editor.services;
            const watermarkHandler = svipWatermark.onWatermark(onChange);
            // 水印事件可能早于 isVip 写回，同时监听当前图层，避免漏掉后到的权限变化。
            const layerHandler = flatLayer.onChange(onChange, layerId);
            return () => {
                events.off(watermarkHandler);
                events.off(layerHandler);
            };
        },
        [editor, layerId],
    );
    const getSnapshot = useCallback(() => {
        const layer = editor.services.flatLayer.getLayer(layerId);
        if (!layer) return null;
        if (editor.groupLayer.checkIsExtraGroup(layer)) return 'create';
        return editor.groupLayer.checkBroken(layer) ? 'break' : 'disabled';
    }, [editor, layerId]);

    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
